import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { LocationContext } from "./LocationContext";

const UploadMediaDialog = ({
  fetchCaseData,
  open,
  onClose,
  data,
  userId = "user1",
}) => {
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(true);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const cameraRef = useRef();
  const videoRef = useRef();
  const { location } = useContext(LocationContext);

  useEffect(() => {
    if (data?.length > 0) {
      setMediaFiles(data);
    }
  }, [data]);

  const handleCaptureInput = (event) => {
    const file = event.target.files[0];
    if (file) {
      setMediaFiles((prevMedia) => [...prevMedia, file]);
    }
  };

  const handleRemoveMedia = (index) => {
    const updatedMediaFiles = [...mediaFiles];
    URL.revokeObjectURL(updatedMediaFiles[index]);
    updatedMediaFiles.splice(index, 1);
    setMediaFiles(updatedMediaFiles);
  };

  const handleUpload = async () => {
    const actualUserId = isAnonymous ? "Anonymous" : userId;

    if (!actualUserId || mediaFiles.length === 0) {
      alert("Please provide a user ID and add at least one file.");
      return;
    }

    const category = "Open";
    setUploading(false);

    const caseId = Date.now().toString();
    const latitude = location?.latitude || 0.0;
    const longitude = location?.longitude || 0.0;
    const city = location?.city || "Unknown";

    const metadataObject = {
      caseid: caseId,
      date: new Date().toISOString(),
      description: description || "No description provided",
      status: "Pending",
      state: location?.state || "Unknown",
      latitude,
      longitude,
      city,
    };

    const metadataBlob = new Blob([JSON.stringify(metadataObject)], {
      type: "application/json",
    });

    const formData = new FormData();
    formData.append("jsonFile", metadataBlob, "metadata.json");
    mediaFiles.forEach((file) => formData.append("files", file));

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(
        `${apiBaseUrl}/api/upload/${actualUserId}/${category}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response) {
        handleCloseDialog();
      }

      alert("Files and metadata uploaded successfully! Classification running in background.");
      fetchCaseData();
    } catch (error) {
      console.error("Error uploading files and metadata:", error);
      alert(
        error.response?.data?.error ||
          "Failed to upload files and metadata. Please check the network and server logs."
      );
    } finally {
      setUploading(true);
    }
  };

  const handleCloseDialog = () => {
    setDescription("");
    setMediaFiles([]);
    setIsAnonymous(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">Capture Media</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div style={{ height: "100%", padding: "5px" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
              />
            }
            label="Post Anonymously"
            sx={{ marginBottom: 1 }}
          />

          <TextField
            id="outlined-basic"
            label="Description"
            onChange={(e) => setDescription(e.target.value)}
            value={description}
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            sx={{ marginBottom: 2 }}
          />
        </div>

        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Media Preview
        </Typography>
        <Grid container spacing={2}>
          {mediaFiles.map((file, index) => (
            <Grid item xs={4} key={index}>
              <Box sx={{ position: "relative" }}>
                {file.type.startsWith("image") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index}`}
                    style={{
                      width: "100%",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  <video
                    src={URL.createObjectURL(file)}
                    controls
                    style={{
                      width: "100%",
                      height: "100px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                )}
                <IconButton
                  onClick={() => handleRemoveMedia(index)}
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "16px",
            padding: "16px 0",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => document.getElementById("file-input").click()}
              sx={{
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Attach File
            </Button>

            <input
              id="file-input"
              type="file"
              accept="image/*,video/*"
              onChange={handleCaptureInput}
              style={{ display: "none" }}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => cameraRef.current.click()}
              sx={{
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Capture Image
            </Button>

            <input
              type="file"
              accept="image/*"
              ref={cameraRef}
              capture="environment"
              onChange={handleCaptureInput}
              style={{ display: "none" }}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => videoRef.current.click()}
              sx={{
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Capture Video
            </Button>

            <input
              type="file"
              accept="video/*"
              ref={videoRef}
              capture="environment"
              onChange={handleCaptureInput}
              style={{ display: "none" }}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleUpload}
              disabled={!uploading}
              sx={{
                padding: "10px 20px",
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "14px",
                borderRadius: "8px",
              }}
            >
              Upload
            </Button>
          </Box>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadMediaDialog;
